import {
	IntegrationFlowAuthenticationKind,
	IntegrationFlowAuthenticationIsRequiredModel
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import {
	Button,
	Dialog,
	FormattedText,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { navigateToUriInPopOutWindow } from "client_legacy/ndaw/navigator";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import styles from "./IntegrationFlowAuthenticationRequiredDialog.module.less";
import { isNotNil, setTrackingId } from "client/Misc/utils";
import { useDataInputDialog } from "client/Hooks";
import { LinkKind, useLinkToIntegrationCredentialsDialog } from "../IntegrationCredentials/LinkToIntegrationCredentialsDialog";
import { useAfasAuthenticate } from "./useAfasAuthenticate";

export type IntegrationFlowAuthenticationRequiredDialogProps = {
	integrationName?: string;
	missingAuthentications: readonly IntegrationFlowAuthenticationIsRequiredModel[];
	onFinish(): void;
	onClose(): void;
};

export const IntegrationFlowAuthenticationRequiredDialog = (props: IntegrationFlowAuthenticationRequiredDialogProps): ReactElement => {
	const {
		integrationName,
		missingAuthentications,
		onFinish,
		onClose
	} = props;

	const [triedAuthenticationIds, setTriedAuthenticationIds] = useState<ReadonlySet<number>>(new Set());
	const [completedAuthenticationIds, setCompletedAuthenticationIds] = useState<ReadonlySet<number>>(new Set());

	const [linkToCredentialsDialog, openLinkToCredentialsDialog] = useLinkToIntegrationCredentialsDialog();

	const {
		authenticate: afasAuthenticate,
		afasOtpDialog
	} = useAfasAuthenticate();

	const clickHandler = useCallback(
		async (authentication: IntegrationFlowAuthenticationIsRequiredModel): Promise<void> => {
			const {
				id
			} = authentication;

			switch(authentication.kind) {
				case IntegrationFlowAuthenticationKind.OAuth2:
					navigateToUriInPopOutWindow(authentication.authenticationUrl);
					break;
				case IntegrationFlowAuthenticationKind.Basic:
					const result: TryResult<void> = await openLinkToCredentialsDialog({
						thingToLink: {
							kind: LinkKind.IntegrationFlowAuthentication,
							id: authentication.id
						}
					});

					if (result.wasSuccessful) {
						setCompletedAuthenticationIds(
							(x): ReadonlySet<number> => new Set<number>(x)
								.add(id)
						);
					}

					break;
				case IntegrationFlowAuthenticationKind.Afas:
					const didSucceed: boolean = await afasAuthenticate(authentication.id);

					if (didSucceed) {
						setCompletedAuthenticationIds(
							(x): ReadonlySet<number> => new Set<number>(x)
								.add(id)
						);
					}
					break;
				default:
					never(authentication);
			}

			setTriedAuthenticationIds(state => {
				const newSet: Set<number> = new Set(state);

				newSet.add(id);

				return newSet;
			});
		},
		[openLinkToCredentialsDialog, afasAuthenticate]
	);

	const isFinishDisabled: boolean = triedAuthenticationIds.size !== missingAuthentications.length;

	return (
		<>
			<Dialog
				trackingId="integration-flow-authentication-required-dialog"
				backdrop={
					isNotNil(linkToCredentialsDialog)
						? "invisible"
						: undefined
				}
			>
				<Dialog.Header
					title={localize("_AuthenticationRequired")}
				/>
				<Dialog.Body>
					<p className={styles.description}>
						<FormattedText
							text={localize("_ToUseIntegrationNameYouNeedToCompleteTheAuthenticationsBelowInfo")}
							thingsToReplace={[
								{
									what: "{{IntegrationName}}",
									filler: (
										<b>{integrationName ?? localize("_ThisIntegration")}</b>
									)
								},
							]}
						/>
					</p>
					<ul>
						{missingAuthentications.map(
							x => (
								<li
									{...setTrackingId("authentication", `|${x.id}`)}
									key={x.id}
									className={styles.item}
								>
									<h4 className={styles.title}>
										{x.name}
									</h4>
									<Button
										kind={triedAuthenticationIds.has(x.id)
											? "secondary"
											: "primary"
										}
										text={localize("_Authenticate")}
										onClick={() => clickHandler(x)}
										trackingId="authenticate-button"
										isDisabled={completedAuthenticationIds.has(x.id)}
									/>
								</li>
							)
						)}
					</ul>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_Finish")}
							onClick={onFinish}
							isDisabled={isFinishDisabled}
							trackingId="validate"
						/>
						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>

			{linkToCredentialsDialog}
			{afasOtpDialog}
		</>
	);
};
IntegrationFlowAuthenticationRequiredDialog.displayName = "IntegrationFlowAuthenticationRequiredDialog";

type InitialIntegrationFlowAuthenticationRequiredDialogProps = Omit<IntegrationFlowAuthenticationRequiredDialogProps, "onClose" |"onFinish">;

export function useIntegrationFlowAuthenticationRequiredDialog(): [
	element: ReactElement | null,
	open: (props: InitialIntegrationFlowAuthenticationRequiredDialogProps) => Promise<TryResult<void>>
] {
	return useDataInputDialog(
		(props, onClose, onFinish) => (
			<IntegrationFlowAuthenticationRequiredDialog
				{...props}
				onFinish={onFinish}
				onClose={onClose}
			/>
		)
	);
}
